import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataService } from '@app/core/services/data.service';
import { LiveDTO } from '@app/modules/live/dtos/live';
import { forkJoin, lastValueFrom, mergeMap, Observable, ReplaySubject, Subject } from 'rxjs';
import { BadisOauthLoginService } from '../auth/badis-oauth-login.service';
import { ServiceBase } from '../service-base.service';
import { SettingsDTO } from './dtos/settings-dto';
import { SettingsModel } from './dtos/settings-model';
import { ValueTypes } from '@app/core/constants/global-enum';


@Injectable({
    providedIn: 'root'
})
export class SettingsService extends ServiceBase {

    public settingChangedBehaivor = new ReplaySubject<SettingsModel.Settings>();
    public settingChanged = this.settingChangedBehaivor.asObservable();

    public settings: SettingsModel.Settings[] = [];

    constructor(
        private dataService: DataService,
        private authService: BadisOauthLoginService) {
        super();

        SettingsModel.Settings.settingChanged.subscribe((setting) => {

            if (!setting) { return; }

            if (setting.scope === SettingsDTO.SettingScope.system) {
                if (setting.externalApplicationId) {
                    this.storeExternalApplicationSetting(setting);
                } else {
                    this.storeSetting(setting);
                }
            } else if (setting.scope === SettingsDTO.SettingScope.user) {
                if (setting.externalApplicationId) {
                    this.storeExternalApplicationUserSetting(setting);
                } else {
                    this.storeUserSetting(setting);
                }
            }

            setting.changeRequest = LiveDTO.ChangeRequest.User;
            this.settingChangedBehaivor.next(setting);
        });

    }

    initSettings(defaultSettings: SettingsDTO.Settings[], userSettings: SettingsDTO.UserSettings[]): void {
        // Create Settings models first
        this.settings = defaultSettings.select(p => new SettingsModel.Settings(p)).toArray();

        // Seed user settings mapping if present
        if (userSettings && Array.isArray(userSettings)) {
            userSettings.forEach((p) => {
                const s = this.settings.firstOrDefault(q => q.id === p.settingId);
                if (s) {
                    s.userSetting.userId = p.userId;
                    s.userSetting.setIntialValue(SettingsModel.Utils.resolveValue(p.primaryValue, s.valueType));
                    // Also update effective primary value for user-scoped settings
                    if (s.scope === SettingsDTO.SettingScope.user) {
                        s.setIntialValue(SettingsModel.Utils.resolveValue(p.primaryValue, s.valueType));
                    }
                }
            });
        }

        // subscribe olunan yerde scope'a bakıp system veya user settings kullanılabilir
        this.settings.forEach((setting) => {
            setting.changeRequest = LiveDTO.ChangeRequest.Initial;
            this.settingChangedBehaivor.next(setting);
        });


    }

    public refresh(): void {
        const serviceSubject = new Subject<void>();
        const defaultSettings = this.dataService.getAny<SettingsDTO.Settings[]>('settings/3'); // Varsayılan sistem ayarları

        let requests = {
            defaultSettings: defaultSettings
        };

        if (this.authService.userAccessToken && this.authService.userAccessToken.userId) {
            // Kullanıcı oturumu varsa, kullanıcı ayarlarını da al
            const userSettings = this.dataService.getAny<SettingsDTO.UserSettings[]>('settings/user/' + this.authService.userAccessToken.userId);
            requests['userSettings'] = userSettings;
        }

        serviceSubject.pipe(mergeMap(() => forkJoin(requests)))
            .subscribe({
                next: (result: any): void => {
                    console.log('Settings loaded from backend:', result);
                    // Eğer kullanıcı ayarları yoksa, sadece varsayılan ayarları kullan
                    this.initSettings(result.defaultSettings, result.userSettings || []);
                    this.readyBehavior.next(true);
                    this.readyBehavior.complete();
                },
                error: (error: any): void => {
                    console.error('Error loading settings from backend:', error);
                    // Hata durumunda boş ayarlarla başlat
                    this.initSettings([], []);
                    this.readyBehavior.next(true);
                    this.readyBehavior.complete();
                }
            });

        serviceSubject.next();
        serviceSubject.complete();
    }

    storeUserSetting(setting: SettingsModel.Settings): void {
        if (!setting) { return; }

        const putResponse = this.dataService.put<any>('settings/user', {
            userId: this.authService.userAccessToken.userId,
            settingId: setting.id,
            primaryValue: setting.primaryValue !== null ? `${setting.primaryValue}` : null
        });

        const result = lastValueFrom(putResponse);
        result.then((data) => { }).catch(() => { });
        this.settingChangedBehaivor.next(setting);
    }

    storeSetting(setting: SettingsModel.Settings): void {
        if (!setting) { return; }

        const model = {
            id: setting.id,
            primaryValue: setting.primaryValue !== null ? `${setting.primaryValue}` : null
        };

        const putResponse = this.dataService.put<any>('settings', model);
        const result = lastValueFrom(putResponse);
        result.then((data) => { }).catch(() => { });
        this.settingChangedBehaivor.next(setting);
    }

    storeExternalApplicationSetting(setting: SettingsModel.Settings): void {
        if (!setting || !setting.externalApplicationId) { return; }

        const model = {
            id: setting.id,
            primaryValue: setting.primaryValue !== null ? `${setting.primaryValue}` : null
        };

        const putResponse = this.dataService.put<any>(`settings/external-application/${setting.externalApplicationId}`, model);
        const result = lastValueFrom(putResponse);
        result.then((data) => { }).catch(() => { });
        this.settingChangedBehaivor.next(setting);
    }

    storeExternalApplicationUserSetting(setting: SettingsModel.Settings): void {
        if (!setting || !setting.externalApplicationId) { return; }

        const putResponse = this.dataService.put<any>(`settings/external-application/${setting.externalApplicationId}/user`, {
            userId: this.authService.userAccessToken.userId,
            settingId: setting.id,
            primaryValue: setting.primaryValue !== null ? `${setting.primaryValue}` : null
        });

        const result = lastValueFrom(putResponse);
        result.then((data) => { }).catch(() => { });
        this.settingChangedBehaivor.next(setting);
    }

    propagateSetting(settingId: string): void {
        const putResponse = this.dataService.post<any>('settings/propagate', JSON.stringify(settingId));
        const result = lastValueFrom(putResponse);
        result.then((data) => { }).catch(() => { });
    }

    public getSetting(key: string): SettingsModel.Settings {
        if (!this.settings) { return null; }
        return this.settings.where(p => p.primaryKey === key).firstOrDefault();
    }

    public getSettingValue<T>(key: string): T {
        const setting = this.getSetting(key);
        if (!setting) { return null; }
        return setting.primaryValue as T;
    }

    public getSettingValueFromId(id: string): any {
        if (!this.settings) { return null; }
        const setting = this.settings.where(p => p.id === id).firstOrDefault();
        if (!setting) { return null; }
        return setting.primaryValue;
    }

    setSettingValue(key: string, value: boolean): void {
        const setting = this.getSetting(key);
        if (!setting) { return null; }
        setting.primaryValue = value;
    }

    isBrowsable(key: string): boolean {
        const setting = this.getSetting(key);
        if (!setting) { return false; }
        return setting.browsable;
    }

    getRelatedItems(additionalData: SettingsModel.ReferenceTableData | String): Observable<SettingsDTO.GenericItem[]> {
        let queryParams = new HttpParams();
        const propNames = Object.getOwnPropertyNames(additionalData);
        propNames.forEach(
            (propName) => {
                const propValue = additionalData[propName];
                if (propValue) { queryParams = queryParams.append(propName, propValue); }
            }
        );

        const queryUrl: any = `helper-service/get-items?${queryParams.toString()}`;
        return this.dataService.get<SettingsDTO.GenericItem[]>(queryUrl, false);
    }

    getEnumValues(enumType: string): Observable<SettingsDTO.GenericItem[]> {
        return this.dataService.get<SettingsDTO.GenericItem[]>(`helper-service/get-enum-values/${enumType}`, false);
    }



    // External Application Settings API'leri de mevcutta kalacak
    public getExternalApplicationSettings(externalApplicationId: string): Observable<SettingsDTO.Settings[]> {
        return this.dataService.get<SettingsDTO.Settings[]>(`settings/external-application/${externalApplicationId}`);
    }

    public getExternalApplicationUserSettings(externalApplicationId: string, userId: string): Observable<SettingsDTO.UserSettings[]> {
        return this.dataService.get<SettingsDTO.UserSettings[]>(`settings/external-application/${externalApplicationId}/user/${userId}`);
    }

    public getExternalApplicationSettingsFromGroup(externalApplicationId: string, groupKey: string): Observable<SettingsDTO.Settings[]> {
        return this.dataService.get<SettingsDTO.Settings[]>(`settings/external-application/${externalApplicationId}/group/${groupKey}`);
    }

    public createExternalApplicationSetting(externalApplicationId: string, setting: SettingsDTO.Settings): Observable<SettingsDTO.Settings> {
        return this.dataService.post<SettingsDTO.Settings>(`settings/external-application/${externalApplicationId}`, setting);
    }

    public deleteExternalApplicationSetting(externalApplicationId: string, settingId: string): Observable<void> {
        return this.dataService.delete<void>(`settings/external-application/${externalApplicationId}/${settingId}`);
    }

    public propagateExternalApplicationSetting(externalApplicationId: string, settingId: string): Observable<void> {
        return this.dataService.post<void>(`settings/external-application/${externalApplicationId}/propagate`, JSON.stringify(settingId));
    }

    public getExternalApplicationSettingValue<T>(externalApplicationId: string, key: string): Observable<T> {
        return this.dataService.get<T>(`settings/external-application/${externalApplicationId}/value/${key}`);
    }

    public setExternalApplicationSettingValue(externalApplicationId: string, key: string, value: any): Observable<void> {
        return this.dataService.put<void>(`settings/external-application/${externalApplicationId}/value/${key}`, { value });
    }


}
